import Link from 'next/link';
import { useState } from 'react';
import { PiPaperPlaneTilt } from 'react-icons/pi';
import { LiaExchangeAltSolid } from 'react-icons/lia';
import { getUrls } from '@shared/utils/config/environment';
import { ChainId } from '@shared/utils/config/networks';
import { useQuery } from '@tanstack/react-query';
import { IoTriangleSharp } from 'react-icons/io5';
import { useRouter } from 'next/router';
import { Button } from '@/components/ui/button';
import { toLocaleShortString, toRate, toSpecialFixed } from '@/lib/misc/utils';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import classNames from '@/lib/misc/classNames';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useSession } from '@/contexts/LitSessionProvider';
import CustomDialog from '@/components/ui/customDialog';
import DTFlexYourGains from './DTFlexYourGains';
import { TPnLData } from '@/components/asset/UserPositionSimpleView';
import TokenToSend from './TokenToSend';
import { ImageWithFallback } from '@/components/shared/NextImageFallback';
import { Skeleton } from '@/components/ui/skeleton';
import { getTokenImage } from '@/lib/asset/nativeAddressHelper';
interface PortfolioAssetItemProps {
  assetItem: any;
  isSwap: boolean;
  assetData: any;
  setLinkClickedLoading?: (boolean) => void;
}
export const PorfolioAssetsTable = ({
  isSwap,
  assetItem,
  setLinkClickedLoading
}: PortfolioAssetItemProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showBuyTooltip, setShowBuyTooltip] = useState(false);
  const [showSellTooltip, setShowSellTooltip] = useState(false);
  const [showSwapTooltip, setShowSwapTooltip] = useState(false);
  const [showSendTooltip, setShowSendTooltip] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  function toggleSendModal() {
    setShowSendModal(!showSendModal);
    setShowTooltip(false);
    setShowSendTooltip(false);
  }
  const router = useRouter();
  const {
    PKP
  } = useSession();
  const {
    data: tokenPnLData,
    isLoading
  } = useQuery({
    queryKey: ['pnlData', assetItem?.contract_address, assetItem?.network],
    queryFn: async (): Promise<TPnLData | undefined> => {
      const data = await fetch(`${getUrls().INDEXER_URL}/portfolio/holdings?address=${assetItem?.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP?.additionalEthAddress}&token=${assetItem?.contract_address}&chains=${assetItem?.network}`).then(res => res.json());
      const pnls = data?.pnls?.[0] ?? null;
      return pnls;
    },
    refetchInterval: 10000
  });
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const rateChange = toRate(assetItem.quote_rate, assetItem.quote_rate_24h);
  const tokenImage = getTokenImage(assetItem);
  function handleClick() {
    if (setLinkClickedLoading) {
      setLinkClickedLoading(true);
    }
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.portfolio.clickToken,
      category: CATEGORIES.portfolio,
      metaData: {
        tokenAddress: assetItem.contract_address,
        networkId: assetItem.network
      }
    });
  }
  const currentGains = toSpecialFixed(tokenPnLData?.realisedPNL + tokenPnLData?.unrealisedPNL)?.value?.replace(',', '');
  const [forceRender, setForceRender] = useState(false);
  return <tbody onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className='hover:bg-genius-blue relative' data-sentry-component="PorfolioAssetsTable" data-sentry-source-file="PortfolioAssetsTable.tsx">
      {showTooltip && <tr>
          <td className='absolute bottom-[-5px] right-[100px] flex items-center justify-center bg-black rounded-full border-genius-blue border-[1px] h-[32px] w-[110px] gap-[1.5px]'>
            <Link href={`/asset?address=${assetItem.contract_address}&networkId=${assetItem.network}&tradeSide=buy`}>
              <div onMouseEnter={() => setShowBuyTooltip(true)} onMouseLeave={() => setShowBuyTooltip(false)} className='w-[24px] h-[24px] relative bg-genius-blue hover:bg-[#34D399] hover:text-genius-blue rounded-full flex justify-center items-center'>
                <div className={`${showBuyTooltip ? 'visible' : 'hidden'} absolute top-[-25px] text-[12px] h-[21px] bg-[#34D399] text-genius-blue flex items-center px-2 rounded-md left-[-7px]`}>
                  Buy
                </div>
                <div className='cursor-pointer h-[14px] w-[14px] p-[1px] rounded-full items-center flex justify-center'>
                  +
                </div>
              </div>
            </Link>
            <Link href={`/asset?address=${assetItem.contract_address}&networkId=${assetItem.network}&tradeSide=sell`}>
              <div onMouseEnter={() => setShowSellTooltip(true)} onMouseLeave={() => setShowSellTooltip(false)} className='cursor-pointer w-[24px] h-[24px] relative bg-genius-blue hover:bg-[#F87171] hover:text-genius-blue rounded-full flex justify-center items-center'>
                <div className={`${showSellTooltip ? 'visible' : 'hidden'} absolute top-[-25px] text-[12px] h-[21px] bg-[#F87171] text-genius-blue flex items-center px-2 rounded-md left-[-7px]`}>
                  Sell
                </div>
                <div className='h-[14px] w-[14px] p-[1px] rounded-full items-center flex justify-center'>
                  -
                </div>
              </div>
            </Link>
            <Link href={`/trade?address=${assetItem.contract_address}&networkId=${assetItem.network}&side=from`}>
              <div onMouseEnter={() => setShowSwapTooltip(true)} onMouseLeave={() => setShowSwapTooltip(false)} className='cursor-pointer w-[24px] h-[24px] relative bg-genius-blue hover:bg-genius-pink hover:text-genius-blue rounded-full flex justify-center items-center'>
                <div className={`${showSwapTooltip ? 'visible' : 'hidden'} absolute top-[-25px] text-[12px] h-[21px] bg-genius-pink text-genius-blue flex items-center px-2 rounded-md left-[-7px]`}>
                  Swap
                </div>
                <div className='h-[14px] w-[14px] p-[1px] rounded-full items-center flex justify-center'>
                  <LiaExchangeAltSolid />
                </div>
              </div>
            </Link>
            <div onClick={toggleSendModal} onMouseEnter={() => setShowSendTooltip(true)} onMouseLeave={() => setShowSendTooltip(false)} className='cursor-pointer w-[24px] h-[24px] relative bg-genius-blue hover:bg-genius-pink hover:text-genius-blue rounded-full flex justify-center items-center'>
              <div className={`${showSendTooltip ? 'visible' : 'hidden'} absolute top-[-25px] text-[12px] h-[21px] bg-genius-pink text-genius-blue flex items-center px-2 rounded-md left-[-7px]`}>
                Send
              </div>
              <div className='h-[14px] w-[14px] p-[1px] rounded-full items-center flex justify-center m-auto'>
                <PiPaperPlaneTilt />
              </div>
            </div>
          </td>
        </tr>}

      <CustomDialog isOpen={showSendModal} toggleModal={toggleSendModal} className={'p-0 h-[522px] w-[572px]'} data-sentry-element="CustomDialog" data-sentry-source-file="PortfolioAssetsTable.tsx">
        <TokenToSend address={assetItem.contract_address} network={assetItem.network} data-sentry-element="TokenToSend" data-sentry-source-file="PortfolioAssetsTable.tsx" />
      </CustomDialog>
      <tr className='cursor-pointer' onClick={(e: any) => {
      const isFlexButton = e.target.classList.contains('flexButton');
      const clickOffDialog = e.target.classList.contains('dialog-layout');
      if (isFlexButton || clickOffDialog) {
        return;
      } else {
        handleClick();
        router.push(`/asset?address=${assetItem.contract_address}&networkId=${assetItem.network}`);
      }
    }}>
        <td>
          <div className='w-full  flex items-center justify-between  min-h-[70px] pl-4'>
            <div className={classNames(`${!isSwap ? 'w-5/12' : 'w-3/4'} cursor-pointer`)}>
              <div className='flex items-center relative'>
                <ImageWithFallback src={tokenImage} className='shrink-0 w-[24px] h-[24px] rounded-full' alt='network logo' width={24} height={24} data-sentry-element="ImageWithFallback" data-sentry-source-file="PortfolioAssetsTable.tsx" />
                <ImageWithFallback src={getNetworkLogo(assetItem?.network)} className='w-[16px] h-[16px] bottom-1 left-4 absolute shrink-0' alt='Network logo' width={10} height={10} data-sentry-element="ImageWithFallback" data-sentry-source-file="PortfolioAssetsTable.tsx" />
                <div className='ml-3 flex flex-col items-start whitespace-nowrap'>
                  <p className='text-[14px]'>{toLocaleShortString(assetItem?.contract_name, 12)}</p>
                  <p className='text-[12px] text-genius-cream/50'>
                    {toLocaleShortString(assetItem?.contract_ticker_symbol)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </td>
        <td className='ta-r'>
          <p className='text-[16px]'>{`$${toSpecialFixed(assetItem.usd_value, 2)?.value}`}</p>
          <p className='text-xs text-genius-cream/50'>
            {`${toSpecialFixed(assetItem.formatted_balance, 2)?.value} ${toLocaleShortString(assetItem.contract_ticker_symbol, 3, true)}`}
          </p>
        </td>
        <td className='ta-r'>
          <p className='text-[16px]'>{`$${toSpecialFixed(assetItem.quote_rate, 2)?.value}`}</p>
          <div className='flex items-center justify-end text-[12px]'>
            {rateChange > 0 ? <>
                <IoTriangleSharp color='#34D399' size={12} />
                <p className='text-green-400 ml-1'>{`${toSpecialFixed(rateChange, 2)?.value}`}%</p>
              </> : <>
                <IoTriangleSharp color='#FF5B5B' size={12} />
                <p className='text-red-400 ml-1'>
                  {`${toSpecialFixed(Math.abs(rateChange), 2)?.value}`}%
                </p>
              </>}
          </div>
        </td>
        <td className='ta-r px-4'>
          {isNaN(parseFloat(currentGains)) ? <div className='flex flex-col items-end justify-end'>
              <Skeleton className='w-20 h-4' />
              <Skeleton className='w-14 h-4 mt-1' />
            </div> : <>
              {' '}
              <p className={`text-[16px]`}>
                {Number(currentGains) > 0 && <span>+</span>}{' '}
                {('$' + toSpecialFixed(parseFloat(currentGains), 2).value).replace('$-', '- $')}
              </p>
              <div className='flex justify-end items-center ta-r text-[12px]'>
                {tokenPnLData?.actualPnLChangePercent > 0 ? <>
                    <IoTriangleSharp color='#34D399' size={12} />
                    <p className='text-green-400 ml-1'>
                      {toSpecialFixed(tokenPnLData?.actualPnLChangePercent, 2).value}%
                    </p>
                  </> : <>
                    <IoTriangleSharp color='#FF5B5B' className='rotate-180	' size={12} />
                    <p className='text-red-400 ml-1'>
                      {toSpecialFixed(tokenPnLData?.actualPnLChangePercent, 2).value.replace('-', '')}
                      %
                    </p>
                  </>}
              </div>
            </>}
        </td>
        <td className='ta-r'>
          <CustomDialog hideClose={true} key={assetItem.contract_address + assetItem.network} className={'p-0 bg-transparent border-none shadow-none'} trigger={<Button className='flexButton bg-genius-blue float-right mr-4 hover:bg-genius-pink hover:text-genius-blue rounded-full w-[91px] h-[24px] flex items-center' variant='ghost' onClick={() => setForceRender(true)}>
                Flex
                <p className='ml-2 flexButton'>🎉</p>
              </Button>} data-sentry-element="CustomDialog" data-sentry-source-file="PortfolioAssetsTable.tsx">
            {forceRender && <DTFlexYourGains key={assetItem.contract_address + assetItem.network} tokenData={assetItem} tokenPnLData={tokenPnLData} isLoading={isLoading} />}
          </CustomDialog>
        </td>
      </tr>
    </tbody>;
};